import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Key, Plus, Trash2, Eye, EyeOff, Copy, Check, AlertTriangle } from "lucide-react";
import PortalLayout from "@/components/portal/PortalLayout";
import { useToast } from "@/components/ui/use-toast";

interface APIKey {
  id: string;
  name: string;
  key: string;
  created_at: string;
  last_used: string | null;
}

const STORAGE_KEY = "intentdrive_api_keys";

function generateKey(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  const hex = Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return `sk_live_${hex.substring(0, 32)}`;
}

function loadKeys(): APIKey[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return [];
}

function saveKeys(keys: APIKey[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(keys));
}

function maskKey(key: string): string {
  return key.substring(0, 12) + "..." + key.substring(key.length - 4);
}

// ---- Code snippets ----
const ENDPOINT = "https://khwvlnkrtrwvfbnnjsrc.supabase.co/functions/v1/scoring-next";

const curlSnippet = `curl -X POST "${ENDPOINT}" \\
  -H "Content-Type: application/json" \\
  -H "apikey: YOUR_ANON_KEY" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -d '{
    "vertical": "solar",
    "fields": {
      "zip": "90210",
      "homeowner": true,
      "electric_bill": 200
    }
  }'`;

const jsSnippet = `const response = await fetch("${ENDPOINT}", {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    "apikey": "YOUR_ANON_KEY",
    "Authorization": "Bearer YOUR_API_KEY",
  },
  body: JSON.stringify({
    vertical: "solar",
    fields: {
      zip: "90210",
      homeowner: true,
      electric_bill: 200,
    },
  }),
});

const data = await response.json();
console.log(data);`;

const pythonSnippet = `import requests

response = requests.post(
    "${ENDPOINT}",
    headers={
        "Content-Type": "application/json",
        "apikey": "YOUR_ANON_KEY",
        "Authorization": "Bearer YOUR_API_KEY",
    },
    json={
        "vertical": "solar",
        "fields": {
            "zip": "90210",
            "homeowner": True,
            "electric_bill": 200,
        },
    },
)

data = response.json()
print(data)`;

const exampleResponse = `{
  "score": 82,
  "tier": "A",
  "signals": {
    "zip_quality": "high",
    "homeowner_match": true,
    "bill_range": "premium"
  },
  "routed_to": "buyer_alpha",
  "bid": 45.00
}`;

// ---- Copy button component ----
function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Button
      size="sm"
      variant="ghost"
      onClick={handleCopy}
      className="text-slate-400 hover:text-emerald-400 h-7 px-2"
    >
      {copied ? (
        <Check className="h-3.5 w-3.5" />
      ) : (
        <Copy className="h-3.5 w-3.5" />
      )}
    </Button>
  );
}

function CodeBlock({ title, code, language }: { title: string; code: string; language: string }) {
  return (
    <div className="rounded-lg border border-slate-700 overflow-hidden">
      <div className="flex items-center justify-between bg-slate-800 px-4 py-2 border-b border-slate-700">
        <span className="text-xs font-medium text-slate-400">{title}</span>
        <CopyButton text={code} />
      </div>
      <pre className="p-4 text-sm text-slate-300 overflow-x-auto bg-slate-900/50">
        <code>{code}</code>
      </pre>
    </div>
  );
}

const PortalAPIKeys = () => {
  const { toast } = useToast();
  const [keys, setKeys] = useState<APIKey[]>(loadKeys);
  const [createOpen, setCreateOpen] = useState(false);
  const [keyName, setKeyName] = useState("");
  const [revealedKeys, setRevealedKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    saveKeys(keys);
  }, [keys]);

  const handleCreate = () => {
    const name = keyName.trim();
    if (!name) {
      toast({ title: "Please enter a key name", variant: "destructive" });
      return;
    }
    const newKey: APIKey = {
      id: crypto.randomUUID(),
      name,
      key: generateKey(),
      created_at: new Date().toISOString(),
      last_used: null,
    };
    setKeys((prev) => [...prev, newKey]);
    setKeyName("");
    setCreateOpen(false);
    // Auto-reveal the new key so the user can copy it
    setRevealedKeys((prev) => new Set(prev).add(newKey.id));
    toast({
      title: "API key created",
      description: "Make sure to copy your key now. You won't be able to see it again.",
    });
  };

  const handleRevoke = (id: string) => {
    setKeys((prev) => prev.filter((k) => k.id !== id));
    setRevealedKeys((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    toast({ title: "API key revoked" });
  };

  const toggleReveal = (id: string) => {
    setRevealedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const copyKey = async (key: string) => {
    await navigator.clipboard.writeText(key);
    toast({ title: "Copied to clipboard" });
  };

  return (
    <PortalLayout>
      <div className="p-6 max-w-5xl mx-auto space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Key className="h-6 w-6 text-emerald-400" />
            API Keys
          </h1>
          <p className="text-slate-400 mt-1">
            Manage API keys for programmatic access to the scoring engine.
          </p>
        </div>

        {/* Keys Table */}
        <Card className="bg-slate-900 border-slate-800">
          <div className="flex items-center justify-between p-4 border-b border-slate-800">
            <h2 className="text-lg font-semibold text-white">Your API Keys</h2>
            <Button
              onClick={() => setCreateOpen(true)}
              className="bg-emerald-600 hover:bg-emerald-700"
              size="sm"
            >
              <Plus className="h-4 w-4 mr-1" /> Generate New Key
            </Button>
          </div>

          {keys.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <Key className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <p>No API keys yet. Generate one to get started.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-800 hover:bg-transparent">
                  <TableHead className="text-slate-400">Name</TableHead>
                  <TableHead className="text-slate-400">Key</TableHead>
                  <TableHead className="text-slate-400">Created</TableHead>
                  <TableHead className="text-slate-400">Last Used</TableHead>
                  <TableHead className="text-slate-400 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {keys.map((apiKey) => {
                  const isRevealed = revealedKeys.has(apiKey.id);
                  return (
                    <TableRow
                      key={apiKey.id}
                      className="border-slate-800 hover:bg-slate-800/50"
                    >
                      <TableCell className="text-white font-medium">
                        {apiKey.name}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <code className="text-sm text-slate-300 bg-slate-800 px-2 py-1 rounded font-mono">
                            {isRevealed ? apiKey.key : maskKey(apiKey.key)}
                          </code>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => toggleReveal(apiKey.id)}
                            className="text-slate-400 hover:text-white h-7 w-7 p-0"
                          >
                            {isRevealed ? (
                              <EyeOff className="h-3.5 w-3.5" />
                            ) : (
                              <Eye className="h-3.5 w-3.5" />
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => copyKey(apiKey.key)}
                            className="text-slate-400 hover:text-emerald-400 h-7 w-7 p-0"
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-400">
                        {new Date(apiKey.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-slate-400">
                        {apiKey.last_used
                          ? new Date(apiKey.last_used).toLocaleDateString()
                          : "Never"}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleRevoke(apiKey.id)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <Trash2 className="h-4 w-4 mr-1" /> Revoke
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Card>

        {/* Rate Limits */}
        <Card className="bg-amber-500/5 border-amber-500/20 p-4 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-300">Rate Limits</p>
            <p className="text-xs text-amber-400/80 mt-0.5">
              20 requests per minute per key. Exceeding the limit returns HTTP 429.
            </p>
          </div>
        </Card>

        {/* API Documentation */}
        <Card className="bg-slate-900 border-slate-800 p-6 space-y-6">
          <h2 className="text-lg font-semibold text-white">API Documentation</h2>

          <div>
            <h3 className="text-sm font-medium text-slate-300 mb-2">Endpoint</h3>
            <div className="flex items-center gap-2">
              <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                POST
              </Badge>
              <code className="text-sm text-slate-300 bg-slate-800 px-3 py-1.5 rounded font-mono break-all">
                {ENDPOINT}
              </code>
              <CopyButton text={ENDPOINT} />
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium text-slate-300 mb-2">
              Required Headers
            </h3>
            <div className="bg-slate-800 rounded-lg p-4 space-y-1 text-sm font-mono text-slate-300">
              <div>
                <span className="text-emerald-400">Content-Type:</span>{" "}
                application/json
              </div>
              <div>
                <span className="text-emerald-400">apikey:</span>{" "}
                <span className="text-slate-500">{"<your_anon_key>"}</span>
              </div>
              <div>
                <span className="text-emerald-400">Authorization:</span>{" "}
                Bearer{" "}
                <span className="text-slate-500">{"<your_api_key>"}</span>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-sm font-medium text-slate-300">Code Examples</h3>

            <CodeBlock title="cURL" code={curlSnippet} language="bash" />
            <CodeBlock title="JavaScript (fetch)" code={jsSnippet} language="javascript" />
            <CodeBlock title="Python (requests)" code={pythonSnippet} language="python" />
          </div>

          <div>
            <h3 className="text-sm font-medium text-slate-300 mb-2">
              Example Response
            </h3>
            <CodeBlock title="JSON Response" code={exampleResponse} language="json" />
          </div>
        </Card>

        {/* Create Key Dialog */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="bg-slate-900 border-slate-800 text-white">
            <DialogHeader>
              <DialogTitle>Generate New API Key</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label className="text-slate-300">Key Name</Label>
                <Input
                  value={keyName}
                  onChange={(e) => setKeyName(e.target.value)}
                  placeholder="e.g. Production, Staging, Partner API"
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                  onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                  autoFocus
                />
                <p className="text-xs text-slate-500 mt-1">
                  Give your key a descriptive name so you can identify it later.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => setCreateOpen(false)}
                className="text-slate-400"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                Generate Key
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </PortalLayout>
  );
};

export default PortalAPIKeys;
